/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Intermediate "choose a company" page shown when a model is offered by more than one rental
 * company. Each card shows the company logo + name, address, how many cars of the model it has
 * available for the dates, and a from-price. Picking a company books a car of that model from it.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, MapPin, Building2 } from 'lucide-react';
import type { ModelCompany } from '../../types';
import { priceUnit, fmtPrice } from '../../utils/pricing';

interface Props {
  model: string;
  companies: ModelCompany[];
  onSelect: (ownerId: string) => void;
}

export default function CompanyPicker({ model, companies, onSelect }: Props) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center text-gray-600 hover:text-blue-600 mb-6">
          <ArrowLeft className="h-5 w-5 mr-2" /> {t('detail.backToSearch')}
        </Link>

        <h1 className="text-2xl font-bold text-gray-900">{t('companies.chooseTitle', { model })}</h1>
        <p className="mt-1 text-gray-600">{t('companies.chooseSubtitle')}</p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {companies.map((c) => {
            const unit = t(`units.${priceUnit(c.pricingPeriod)}`);
            return (
              <div key={c.ownerId} className="bg-white rounded-lg shadow-md p-5 flex flex-col">
                <div className="flex items-center gap-3">
                  {c.logoUrl ? (
                    <img src={c.logoUrl} alt={c.companyName} className="h-12 w-12 rounded-lg object-contain bg-gray-50" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-400">
                      <Building2 size={22} />
                    </div>
                  )}
                  <h3 className="font-bold text-gray-900 leading-tight">{c.companyName}</h3>
                </div>

                {c.address && (
                  <div className="mt-3 flex items-start gap-1.5 text-sm text-gray-600">
                    <MapPin size={15} className="mt-0.5 shrink-0" /> <span>{c.address}</span>
                  </div>
                )}

                <div className="mt-3 text-sm text-gray-700">
                  <span className="font-semibold text-green-600">{t('booking.availableCount', { n: c.availableCount })}</span>
                  {c.minPrice > 0 && (
                    <span className="text-gray-500"> · {t('companies.from')} ${fmtPrice(c.minPrice)}/{unit}</span>
                  )}
                </div>

                <button onClick={() => onSelect(c.ownerId)} className="btn-primary mt-4 w-full py-2.5">
                  {t('companies.select')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
